import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PublicReviews } from '../components/reviews/PublicReviews';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Bus,
  Heart,
  Bell,
  Shield,
  Smartphone,
  Lock,
  MessageSquare,
  Target,
  Eye,
  ArrowLeft,
  Mail,
} from 'lucide-react';

export default function AboutUs() {
  const navigate = useNavigate();
  const isLanding = useLocation().pathname === '/';

  const offerings = [
    { icon: Bell, title: 'Real-time notifications', desc: 'Stay updated the moment something happens.' },
    { icon: MessageSquare, title: 'Secure communication', desc: 'Encrypted messages between parents and providers.' },
    { icon: Eye, title: 'Easy access to information', desc: 'All your child\u2019s details in one place.' },
    { icon: Target, title: 'Efficient management', desc: 'Tools that simplify daily transport operations.' },
    { icon: Smartphone, title: 'Mobile-friendly experience', desc: 'Built for any device, any screen.' },
    { icon: Lock, title: 'Reliable data protection', desc: 'Your family\u2019s data is always secure.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50" data-testid="about-us-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-sm bg-white flex-shrink-0">
              <img src="/ntwanayam-logo.jpg" alt="NtwanaYam Logo" className="w-full h-full object-cover" />
            </div>
            <span className="font-bold text-lg text-foreground">NtwanaYam</span>
          </div>
          <div className="flex items-center gap-2">
            {!isLanding && (
              <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full hidden sm:inline-flex" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            <Link to="/login">
              <Button variant="outline" className="rounded-full" data-testid="login-btn">Sign In</Button>
            </Link>
            <Link to="/register" className="hidden sm:inline-block">
              <Button className="rounded-full" data-testid="register-btn">Register</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 py-12 sm:py-16 lg:py-20 text-center max-w-4xl">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6" data-testid="hero-badge">
          <Heart className="w-4 h-4" /> About Us
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight text-foreground mb-6" data-testid="hero-title">
          Welcome to <span className="text-primary">NtwanaYam</span>
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto" data-testid="hero-subtitle">
          NtwanaYam is a smart and reliable platform designed to help parents, guardians, schools, and
          transport providers stay connected and informed about the safety and well-being of their children.
        </p>
      </section>

      {/* Story */}
      <section className="container mx-auto px-4 pb-12 max-w-4xl">
        <Card className="shadow-lg border-emerald-100">
          <CardContent className="p-6 sm:p-10 space-y-5 text-base leading-relaxed text-foreground/90">
            <p data-testid="story-paragraph-1">
              The name <strong className="text-primary">NtwanaYam</strong>, meaning <em>&quot;My Child&quot;</em>, reflects our
              commitment to putting children first. We understand that every parent wants peace of mind knowing that
              their child is safe, whether travelling to school, participating in activities, or being cared for
              throughout the day.
            </p>
            <p data-testid="story-paragraph-2">
              Our platform leverages modern technology to provide real-time communication, efficient management, and
              improved visibility between all stakeholders involved in a child&apos;s daily journey. Through a
              user-friendly interface, NtwanaYam makes it easier to monitor activities, receive important
              notifications, and access critical information whenever needed.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Mission & Vision */}
      <section className="container mx-auto px-4 pb-12 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="shadow-lg border-emerald-100 hover:shadow-xl transition-shadow" data-testid="mission-card">
            <CardContent className="p-6 sm:p-8">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold mb-3">Our Mission</h2>
              <p className="text-muted-foreground leading-relaxed">
                To provide a secure, innovative, and trusted digital solution that enhances child safety,
                strengthens communication, and simplifies the management of child-related services.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-emerald-100 hover:shadow-xl transition-shadow" data-testid="vision-card">
            <CardContent className="p-6 sm:p-8">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold mb-3">Our Vision</h2>
              <p className="text-muted-foreground leading-relaxed">
                To become the leading child-focused digital platform in South Africa, empowering families,
                schools, and service providers through technology that promotes safety, accountability, and
                peace of mind.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What We Offer */}
      <section className="container mx-auto px-4 pb-12 max-w-6xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3" data-testid="offer-title">What We Offer</h2>
          <p className="text-muted-foreground">Everything you need to keep your child safe and connected.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {offerings.map(({ icon: Icon, title, desc }, idx) => (
            <Card key={title} className="shadow-md hover:shadow-lg transition-all hover:-translate-y-1 duration-300 border-emerald-100" data-testid={`offering-${idx}`}>
              <CardContent className="p-6">
                <div className="w-11 h-11 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-lg mb-1.5">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Commitment */}
      <section className="container mx-auto px-4 pb-12 max-w-4xl">
        <Card className="shadow-lg border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="commitment-card">
          <CardContent className="p-6 sm:p-10">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold mb-2">Our Commitment</h2>
                <p className="text-foreground/90 leading-relaxed">
                  At NtwanaYam, we are committed to building a platform that parents can trust. We continuously
                  strive to improve our services, embrace innovation, and maintain the highest standards of
                  security, reliability, and customer support.
                </p>
              </div>
            </div>
            <div className="border-t border-emerald-200 pt-5 mt-5 text-center">
              <p className="text-lg font-medium text-emerald-700 italic" data-testid="commitment-quote">
                Because every child matters, and every parent deserves peace of mind.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      <PublicReviews />

      {/* Contact Us */}
      <section className="container mx-auto px-4 pb-12 max-w-4xl" data-testid="contact-section">
        <Card className="shadow-lg border-emerald-100">
          <CardContent className="p-6 sm:p-10 text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-2">Contact Us</h2>
            <p className="text-muted-foreground mb-5">
              Have a question, feedback, or partnership enquiry? We&apos;d love to hear from you.
            </p>
            <a
              href="mailto:info@ntwanayam.co.za"
              className="inline-flex items-center gap-2 text-lg font-semibold text-primary hover:underline"
              data-testid="contact-email"
            >
              <Mail className="w-5 h-5" /> info@ntwanayam.co.za
            </a>
          </CardContent>
        </Card>
      </section>

      {/* CTA Footer */}
      <section className="container mx-auto px-4 pb-16 max-w-4xl text-center">
        <p className="text-xl sm:text-2xl font-heading font-bold mb-6" data-testid="tagline">
          NtwanaYam – <span className="text-primary">Keeping You Connected to What Matters Most.</span>
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/register">
            <Button size="lg" className="rounded-full px-8" data-testid="cta-register">
              Get Started
            </Button>
          </Link>
          <Link to="/login">
            <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="cta-login">
              Sign In
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-emerald-100 bg-white/50 py-6">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground space-y-1">
          <p>
            Contact:{' '}
            <a href="mailto:info@ntwanayam.co.za" className="text-primary hover:underline" data-testid="footer-email">
              info@ntwanayam.co.za
            </a>
            <span className="mx-2">·</span>
            <Link to="/terms" className="text-primary hover:underline" data-testid="footer-terms-link">
              Terms &amp; Legal
            </Link>
          </p>
          <p>© {new Date().getFullYear()} NtwanaYam. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
